import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import { listOwnedVaults, updateOwnName } from "@secure-vault/api";
import { Button, ProfilePageView } from "@secure-vault/ui";

import { useAuth } from "@/lib/auth-context";
import { auth } from "@/lib/firebase";
import {
  useUpdateUserSettings,
  useUserSettings,
} from "@/hooks/use-user-settings";

const ownedVaultsKey = (uid?: string | null) =>
  ["vault-owned-vaults", uid ?? "anonymous"] as const;

/**
 * Vault-app profile settings.
 *
 * For a vault OWNER the editable name is their `client.clients`
 * first_name/last_name — the exact record the advisor client list reads — so a
 * rename here now propagates to the advisor (the bug this fixes). We resolve the
 * owner's client record via `listOwnedVaults()` (magic-link auth, works
 * pre-unlock) and persist through `updateOwnName`, which never touches
 * `auth.users`.
 *
 * A trustee-only user has no `client.clients` record, so we fall back to the
 * legacy `auth.users.full_name` field for them.
 */
export function ProfileContainer() {
  const { logout } = useAuth();
  const uid = auth.currentUser?.uid;
  const queryClient = useQueryClient();

  const { data: user, isLoading: isUserLoading } = useUserSettings();

  const { data: ownedVaults, isLoading: areVaultsLoading } = useQuery({
    queryKey: ownedVaultsKey(uid),
    queryFn: listOwnedVaults,
    enabled: !!uid,
    staleTime: 5 * 60 * 1000,
  });

  const ownVault = ownedVaults?.[0] ?? null;

  const { mutate: saveName, isPending: isSavingName } = useMutation({
    mutationFn: (vars: { firstName: string; lastName: string }) => {
      if (!ownVault) throw new Error("No owned vault to rename.");
      return updateOwnName({
        client_id: ownVault.client_id,
        first_name: vars.firstName,
        last_name: vars.lastName,
      });
    },
    onSuccess: () => {
      toast.success("Profile updated.");
      void queryClient.invalidateQueries({ queryKey: ownedVaultsKey(uid) });
    },
    onError: () => toast.error("Failed to update profile."),
  });

  // Fallback path for trustee-only users (no client.clients record).
  const { mutate: updateSettings, isPending: isSavingFullName } =
    useUpdateUserSettings();

  if (isUserLoading || areVaultsLoading) {
    return (
      <div className="flex h-[200px] w-full items-center justify-center">
        <Loader2 className="text-muted-foreground h-8 w-8 animate-spin" />
      </div>
    );
  }

  const signOut = (
    <Button variant="outline" onClick={() => void logout()}>
      Sign out
    </Button>
  );

  if (ownVault) {
    return (
      <div className="space-y-8">
        <ProfilePageView
          firstName={ownVault.first_name}
          lastName={ownVault.last_name}
          email={ownVault.email}
          isSaving={isSavingName}
          onSaveName={(firstName, lastName) =>
            saveName({ firstName, lastName })
          }
          emailCardFooter={signOut}
        />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <ProfilePageView
        fullName={user?.full_name ?? ""}
        email={user?.email ?? ""}
        isSaving={isSavingFullName}
        onSave={(fullName) =>
          updateSettings(
            { full_name: fullName },
            {
              onSuccess: () => toast.success("Profile updated."),
              onError: () => toast.error("Failed to update profile."),
            }
          )
        }
        emailCardFooter={signOut}
      />
    </div>
  );
}
